import type { SegmentResult } from '@/core/data/spark/spark.interface';
import { addHours, differenceInHours, startOfHour } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { getProposalTime } from './proposal-time';
import { makeXorShift128 } from './seed-rng';

const MIN_SUGGESTION_DURATION_MS = 2 * 60 * 60 * 1000;
const CANONICAL_START_HOUR = 8;
const CANONICAL_END_HOUR = 23;

export interface SparkSuggestedProposalTime {
  start: Date;
  end: Date;
}

type SparkSegmentSeedInput = Pick<SegmentResult, 'start' | 'location'> & {
  timeZone?: string;
};

export const getSparkSegmentSeed = (segment: SparkSegmentSeedInput): string => {
  const timeZone = segment.timeZone || 'UTC';
  const dateKey = formatInTimeZone(new Date(segment.start), timeZone, 'yyyy-MM-dd');
  return `${dateKey}-${segment.location.id}`;
};

export const getSparkSuggestedProposalOffset = (segment: SparkSegmentSeedInput): number => {
  return makeXorShift128(getSparkSegmentSeed(segment)).nextFloat();
};

export const getSeededSuggestedProposalTime = (
  segment: SegmentResult,
  offsetHours = getSparkSuggestedProposalOffset(segment),
  now?: Date,
): SparkSuggestedProposalTime => {
  const segmentStart = new Date(segment.start);
  const segmentEnd = new Date(segment.end);
  const fallback = getProposalTime(segment);
  const safeOffset = Number.isFinite(offsetHours) ? Math.min(0.999999999999, Math.max(0, offsetHours)) : 0;

  if (differenceInHours(segmentEnd, segmentStart) < 2) {
    return clampToSegment(fallback, segmentStart, segmentEnd);
  }

  const preferred = pickPreferredDayWindow(segment, safeOffset);
  if (preferred && windowFitsSegment(preferred, segmentStart, segmentEnd) && isUpcoming(preferred.start, now)) {
    return preferred;
  }

  const remainingStart = now && now.getTime() > segmentStart.getTime() ? now : segmentStart;
  const segmentWindow = pickTwoHourWindow(remainingStart, segmentEnd, safeOffset);
  if (segmentWindow) {
    return segmentWindow;
  }

  return clampToSegment(fallback, segmentStart, segmentEnd);
};

export const getLiveSuggestedProposalTime = (segment: SegmentResult, now: Date): SparkSuggestedProposalTime => {
  return getSeededSuggestedProposalTime(segment, getSparkSuggestedProposalOffset(segment), now);
};

function isUpcoming(start: Date, now?: Date): boolean {
  return !now || start.getTime() > now.getTime();
}

function pickPreferredDayWindow(segment: SegmentResult, offsetHours: number): SparkSuggestedProposalTime | null {
  const timeZone = segment.timeZone || 'UTC';
  const dateKey = formatInTimeZone(new Date(segment.start), timeZone, 'yyyy-MM-dd');
  const dayStart = fromZonedTime(`${dateKey}T${String(CANONICAL_START_HOUR).padStart(2, '0')}:00:00`, timeZone);
  const dayEnd = fromZonedTime(`${dateKey}T${String(CANONICAL_END_HOUR).padStart(2, '0')}:00:00`, timeZone);

  return pickTwoHourWindow(dayStart, dayEnd, offsetHours);
}

function pickTwoHourWindow(windowStart: Date, windowEnd: Date, offsetHours: number): SparkSuggestedProposalTime | null {
  const firstHour = startOfHour(windowStart);
  const firstCandidateStart = firstHour < windowStart ? addHours(firstHour, 1) : firstHour;
  const latestPossibleStart = startOfHour(new Date(windowEnd.getTime() - MIN_SUGGESTION_DURATION_MS));

  if (latestPossibleStart < firstCandidateStart) {
    return null;
  }

  const candidateCount = differenceInHours(latestPossibleStart, firstCandidateStart) + 1;
  const randomIndex = Math.min(candidateCount - 1, Math.floor(offsetHours * candidateCount));
  const start = addHours(firstCandidateStart, randomIndex);
  const end = addHours(start, 2);
  const clamped = clampToSegment({ start, end }, windowStart, windowEnd);

  if (clamped.end.getTime() - clamped.start.getTime() < MIN_SUGGESTION_DURATION_MS) {
    return null;
  }

  return clamped;
}

function windowFitsSegment(time: SparkSuggestedProposalTime, segmentStart: Date, segmentEnd: Date): boolean {
  return (
    time.start.getTime() >= segmentStart.getTime() &&
    time.end.getTime() <= segmentEnd.getTime() &&
    time.end.getTime() - time.start.getTime() >= MIN_SUGGESTION_DURATION_MS
  );
}

function clampToSegment(
  time: SparkSuggestedProposalTime,
  segmentStart: Date,
  segmentEnd: Date,
): SparkSuggestedProposalTime {
  const start = time.start < segmentStart ? segmentStart : time.start;
  const end = time.end > segmentEnd ? segmentEnd : time.end;
  return { start, end };
}
